'use client';

import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import Image from '@/components/Image';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import { FC } from 'react';
import Link from '../Link';

export interface TicketingHeroProps {
  title: React.ReactNode;
  description: string;
  eyebrow: string;

  links: ButtonWithLinkOrAction[];

  featuredImage: {
    desktop: {
      url: string;
      width: number;
      height: number;
      alt: string;
    },
    mobile: {
      url: string;
      width: number;
      height: number;
      alt: string;
    }
  };
  decorationImage: {
    desktop: {
      url: string;
      width: number;
      height: number;
      alt: string;
    },
    mobile: {
      url: string;
      width: number;
      height: number;
      alt: string;
    }
  };
}

const TicketingHero: FC<TicketingHeroProps> = ({
  eyebrow,
  title,
  description,
  links = [],
  featuredImage,
  decorationImage,
}) => {
  const { modalActionHandler } = useModalButtonActions();

  return (
    <div className={`bg-green-800 text-white pb-[96px] bg-right-top 3xl:bg-[length:45%_80%] bg-no-repeat md:bg-[url('https://front.com/webassets/rebrand/ticketing-pattern-background.svg')]`}>
      <div className="-mt-header-height-mobile md:-mt-header-height-desktop pt-header-height-mobile md:pt-header-height-desktop">
        <div className="pt-11 md:pt-12 px-container-mobile md:px-container-desktop pb-10">
          <div className="mx-auto max-w-grid grid gap-2 md:gap-3 text-left">
            <div className="flex items-center justify-center md:justify-between flex-wrap md:flex-nowrap">
              <div className="block max-w-[480px] col-start-1 col-end-12 md:col-end-6 ">
                <div className="text-micro1 mb-[12px]">{eyebrow}</div>
                <h1 className="font-500 tracking-tight text-h1 md:text-display3 mb-[16px]">{title}</h1>
                <p className="text-white text-p1 max-w-80 text-pretty mb-[32px]">{description}</p>
                <ul className="flex gap-1 md:gap-2">
                  {links.map((link, index) => {
                    return (
                      <li key={`product-hero-link-${link.title?.replace(/\s+/g, '-') ?? index}`}>
                        {link.href.length ? (
                          <Link
                            className={cn('block button button-size-medium', {
                              'button-primary-white': link.type === 'primary',
                              'button-secondary-citron-300': link.type === 'secondary',
                            })}
                            href={link.href}
                          >
                            {link.title}
                          </Link>
                        ) : (
                          <button
                            className={cn('block button button-size-medium', {
                              'button-primary-citron': link.type === 'primary',
                              'button-secondary-citron-300': link.type === 'secondary',
                            })}
                            onClick={() => {
                              modalActionHandler(link.action);
                            }}
                          >
                            {link.title}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="flex col-start-1 col-end-12 md:col-start-6 justify-end relative left-[-10%] z-10 mt-[96px] md:mt-0">
                <Image src={featuredImage.desktop.url} width={featuredImage.desktop.width} height={featuredImage.desktop.height} alt={featuredImage.desktop.alt} className="z-0 hidden xl:block rounded-[16px]" />
                <Image src={featuredImage.mobile.url} width={featuredImage.mobile.width} height={featuredImage.mobile.height} alt={featuredImage.mobile.alt} className="z-0 block md:hidden" />
                <div className="absolute left-[-10%] top-[65%] md:left-[-30%] md:top-[20%]">
                  <Image src={decorationImage.desktop.url} width={decorationImage.desktop.width} height={decorationImage.desktop.height} alt={decorationImage.desktop.alt} className="z-10 hidden md:block" />
                  <Image src={decorationImage.mobile.url} width={decorationImage.mobile.width} height={decorationImage.mobile.height} alt={decorationImage.mobile.alt} className="z-10 block md:hidden" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketingHero;
